import { useEffect, useState } from 'react';
import type { InvoicePayment } from '../../types';
import { fetchPaymentsForInvoices } from '../../services/invoicePayments';
import { createPaymentsStream, type PaymentsLoad, type PaymentsStream } from './paymentsLoadLogic';

/**
 * Die Zahlungen der Kennzahlen in der Rechnungsuebersicht — Bestand,
 * Ladezustand, Fehlermeldung und die Ladesteuerung samt Abbruch.
 *
 * Aus InvoicesView herausgeloest, damit diese Teile zusammen bleiben: die
 * Steuerung schreibt in genau diese drei Zustaende, und ihr Abbruch beim
 * Verlassen der Ansicht gehoert zu ihr. Die Logik selbst (laufende Nummer,
 * Aussperren alter Antworten) liegt rein und getestet in paymentsLoadLogic.
 *
 * Ausgeloest wird der Abruf weiter von load() in InvoicesView
 * (`paymentsStream.load(ids)`), nicht von hier: nur load() weiss, wann eine
 * neue Belegliste steht.
 */
export const usePaymentsStream = () => {
    const [payments, setPayments] = useState<InvoicePayment[]>([]);
    /**
     * 'loading' heisst: die Belege stehen schon, die Zahlungen fehlen noch.
     *
     * Startwert bewusst 'loading' und nicht 'idle': zwischen dem ersten Rendern
     * und der ersten Antwort liegen die Zahlungen tatsaechlich nicht vor, und
     * 'idle' behauptete in dieser Spanne, die Auswertung sei vollstaendig.
     */
    const [paymentsLoad, setPaymentsLoad] = useState<PaymentsLoad>('loading');
    const [paymentsError, setPaymentsError] = useState<string | null>(null);

    /**
     * Der Abruf der Zahlungen — bewusst NEBEN dem Laden der Liste.
     *
     * Er stand frueher mitten in load() und wurde dort abgewartet; die Tabelle
     * haengt an `loading`, also erschien die Belegliste erst, wenn auch die
     * Zahlungen da waren. Gebraucht werden sie dort gar nicht: die Spalte
     * "Offen" rechnet aus den Summen der Belegzeile (openAmountCents), nicht aus
     * Zahlungen.
     *
     * Gehalten in einem Ref und NICHT in einem useMemo — dieselbe Begruendung
     * wie bei pickerStockRef: React darf einen gemerkten Wert jederzeit
     * wegwerfen, und eine zweite Steuerung wuesste von der laufenden Anforderung
     * der ersten nichts. Damit waere gerade die laufende Nummer wertlos, die
     * hier die alte Antwort aussperrt.
     *
     * Im eigenen Hook steht statt des Refs ein Zustand mit Initialisierer — aus
     * demselben Grund und mit derselben Zusage: React verwirft Zustand nie,
     * solange die Ansicht steht, und der Initialisierer laeuft nur beim ersten
     * Rendern. Der Ref ging hier nicht mehr: ausserhalb von InvoicesView prueft
     * die Compiler-Regel react-hooks/refs den Code, und die verbietet, beim
     * Rendern `ref.current` zu lesen — genau das braucht der Rueckgabewert
     * (paymentsStream steht in Abhaengigkeitslisten). Dasselbe Muster wie in
     * useEmpfaenger und useUnterlagen.
     */
    const [paymentsStream] = useState<PaymentsStream>(() =>
        createPaymentsStream<InvoicePayment>({
            fetchFor: async (ids) => [...(await fetchPaymentsForInvoices(ids)).values()].flat(),
            setPayments,
            setLoad: setPaymentsLoad,
            reportError: (message, cause) => {
                // Sichtbar machen UND protokollieren: das Banner sagt dem
                // Nutzer, dass DSO und Zahlungsmoral fehlen; der urspruengliche
                // Fehler bleibt fuer die Fehlersuche in der Konsole.
                if (cause !== undefined) {
                    console.error(
                        'Zahlungen fuer die Kennzahlen konnten nicht geladen werden.',
                        cause,
                    );
                }
                setPaymentsError(message);
            },
        }),
    );

    // (Gegenstueck zu `pickerStock.close()` beim Verlassen in InvoicesView.)
    // Dasselbe fuer die Zahlungen: sie werden nicht mehr abgewartet, laufen
    // also regelmaessig noch, wenn der Nutzer die Ansicht schon verlassen hat.
    // Der Abbruch verwirft ihr Ergebnis, statt es in Setter zu schreiben, die
    // ins Leere zeigen.
    useEffect(() => () => paymentsStream.cancel(), [paymentsStream]);

    /**
     * Fehlen die Zahlungen noch?
     *
     * Massgeblich fuer die Auswertungen, die ohne sie eine FALSCHE Zahl zeigen
     * wuerden: der durchschnittliche Zahlungseingang in der Kennzahlenleiste
     * und die Zahlungsmoral je Kunde. Beide leiten sich aus dem letzten
     * Zahlungseingang je Beleg ab — ohne Zahlungen gilt kein Beleg als
     * beglichen, und ein puenktlicher Kunde verschwaende aus der Liste, statt
     * dort mit "0 Tage Verzug" zu stehen.
     *
     * Der Fehlschlag ('failed') zaehlt hier bewusst NICHT dazu: dort steht das
     * Banner, das den Fehlbestand benennt, und ein zweiter, dauerhafter
     * Ladehinweis daneben behauptete Fortschritt, den es nicht gibt.
     */
    const paymentsPending = paymentsLoad === 'loading';

    return { payments, setPayments, paymentsLoad, paymentsError, paymentsStream, paymentsPending };
};
